import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { featured } from "@/mocks/featured";
import { getDramaById } from "@/lib/data";

const slides = featured
  .map((f) => ({ ...f, drama: getDramaById(f.dramaId) }))
  .filter((s) => Boolean(s.drama));

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const go = (dir: number) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden bg-primary-950">
      {slides.map((slide, i) => {
        const drama = slide.drama!;
        return (
          <div
            key={slide.dramaId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={slide.backdrop}
              alt={`${drama.title} 剧集剧照`}
              title={`${drama.title} - 漂亮的保姆`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20" />
          </div>
        );
      })}

      <div className="relative z-10 h-full max-w-[1200px] mx-auto px-4 md:px-6 flex flex-col justify-end pb-14 md:pb-20">
        {(() => {
          const slide = slides[index];
          const drama = slide.drama!;
          return (
            <div key={slide.dramaId} className="max-w-2xl animate-fade-up">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                  焦点推荐
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-50/20 text-background-50 text-xs backdrop-blur">
                  {drama.genre}
                </span>
                <span className="text-background-200 text-xs">{drama.year}</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">
                {drama.title}
              </h1>
              <p className="text-background-100 text-sm md:text-base mb-2">
                {slide.tagline}
              </p>
              <p className="text-background-300 text-xs md:text-sm mb-6 line-clamp-2">
                主演：{drama.cast.join(" / ")} · 导演：{drama.director}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/detail/${drama.id}`)}
                  className="h-10 md:h-11 px-5 md:px-6 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看剧情详情
                </button>
                <Link
                  to={`/category/${drama.type}`}
                  className="h-10 md:h-11 px-5 rounded-md bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur flex items-center gap-2"
                >
                  <i className="ri-apps-2-line text-lg" />
                  同类剧集
                </Link>
              </div>
            </div>
          );
        })()}

        <div className="absolute right-4 md:right-6 bottom-6 flex items-center gap-2">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(-1)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/15 text-background-50 flex items-center justify-center text-xl hover:bg-background-50/30 transition-colors cursor-pointer backdrop-blur"
          >
            <i className="ri-arrow-left-s-line" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(1)}
            className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/15 text-background-50 flex items-center justify-center text-xl hover:bg-background-50/30 transition-colors cursor-pointer backdrop-blur"
          >
            <i className="ri-arrow-right-s-line" />
          </button>
        </div>

        <div className="absolute left-4 md:left-6 bottom-6 flex items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.dramaId}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}